import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import {
  sendUserAnswersAction,
  setAnswersAction,
} from 'root-redux/actions/user'
import { selectUUID } from 'root-redux/selects/user'

import { TechnicalIssuesOptionValue } from 'modules/unsubscribe/pages/TechnicalIssues/constants'

import { Events, eventLogger } from 'services/eventLogger.service'

import { PageId } from 'page-constants'
import { Language } from 'root-constants'

import { FlowButton } from '../../components/FlowButton'
import { FlowFeedbackPopup } from '../../components/FlowFeedbackPopup'
import { FlowOption } from '../../components/FlowOption'
import { FlowPageContainer } from '../../components/FlowPageContainer'
import { useCancelSubscription } from '../../hooks/useCancelSubscription'
import { useRetentionFlowV2Config } from '../../hooks/useRetentionFlowV2Config'
import { FlowContent, FlowOptions } from '../../styles'
import { StyledFlowTechnicalIssues as S } from './FlowTechnicalIssues.styles'
import { FLOW_TECHNICAL_ISSUES_VALUES } from './constants'

export const FlowTechnicalIssues: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const userId = useSelector(selectUUID)
  const { appName, copyKey } = useRetentionFlowV2Config()
  const {
    isFeedbackPopupShown,
    askFeedback,
    closeFeedbackPopup,
    cancelSubscription,
  } = useCancelSubscription()

  const [answers, setAnswers] = useState<TechnicalIssuesOptionValue[]>([])

  const handleContinue = () => {
    dispatch(setAnswersAction({ answers, pageId: PageId.TECHNICAL_ISSUES }))
    dispatch(sendUserAnswersAction())

    eventLogger.logQuestion({
      event: Events.UNSUB_FLOW_QUESTION_3_END,
      question: t('retention.technicalIssues.title', { lng: Language.EN }),
      answers: answers
        .map((value) =>
          t(`${copyKey}.technicalIssues.answers.${value}`, {
            lng: Language.EN,
          }),
        )
        .join(' | '),
      userId,
    })

    askFeedback()
  }

  const toggleAnswer = (value: TechnicalIssuesOptionValue) => {
    setAnswers((currentAnswers) =>
      currentAnswers.includes(value)
        ? currentAnswers.filter((item) => item !== value)
        : [...currentAnswers, value],
    )
  }

  return (
    <FlowPageContainer hasNavigation>
      <S.Content>
        <FlowContent>
          <S.Title>{t('retention.technicalIssues.title')}</S.Title>
          <FlowOptions>
            {FLOW_TECHNICAL_ISSUES_VALUES[appName]?.map((value) => (
              <FlowOption
                key={value}
                hasCheckbox
                isSelected={answers.includes(value)}
                onClick={() => toggleAnswer(value)}
              >
                {t(`${copyKey}.technicalIssues.answers.${value}`)}
              </FlowOption>
            ))}
          </FlowOptions>
        </FlowContent>
      </S.Content>
      <S.BottomArea isFixed>
        <FlowButton
          type="button"
          disabled={!answers.length}
          onClick={handleContinue}
        >
          {t('button.continue')}
        </FlowButton>
        <FlowButton type="button" variant="transparent" onClick={askFeedback}>
          {t(`${copyKey}.skip`)}
        </FlowButton>
      </S.BottomArea>
      <FlowFeedbackPopup
        isShown={isFeedbackPopupShown}
        onComplete={cancelSubscription}
        onClose={closeFeedbackPopup}
      />
    </FlowPageContainer>
  )
}
